import React from 'react';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import {Col, Row, Spin} from 'antd';

import StaffActionCard from './StaffActionCard';
import {formatTimestamp} from '../../../../helpers/misc';
import {messageType, showMessages} from '../../../../helpers/message';
import {useLazyGetBookingsQuery} from '../../../../store/api/bookingsApi';
import {
    useCancelMemberBookingsMutation,
    useCloseAccountMutation,
    useDeleteMemberMutation,
    useLazyGetProfileQuery,
    useMigrateMemberToV7Mutation
} from '../../../../store/api/accountApi';
import {usePutInJailMutation} from '../../../../store/api/jailApi';
import {useAppDispatch, useAppSelector} from '../../../../hooks/useStore';
import {
    clearActiveMemberStaff,
    selectActiveMembershipId,
    selectCurrentProfile,
    selectHasStaffRole
} from '../../../../store/slices/userSlice';
import {useHasJail} from '../../../../hooks/useGetJails';
import {AccountOrProfile} from '../../../../types/accountTypes';
import {BaseError} from '../../../../types/apiTypes';
import {
    cancelMemberBookingsSuccessMessage, cancelMemberBookingsToolTip, closeAccountDescription,
    closeAccountSuccessMessage, closeAccountTooltip,
    deleteApplicantDescription, deleteApplicantToolTip,
    makeTaxExemptToolTip, migrateToV7SuccessMessage,
    migrateToV7ToolTip,
    restrictionAddedByStaff, restrictionAppliedSuccessMessage,
    revokeCreateBookingToolTip, taxExemptSuccessMessage, waiveMonthlyFeeSuccessMessage,
    waiveMonthlyFeeToolTip
} from '../../../../helpers/text';

import {staffActionColSize} from '../../../../assets/styles';
import './StaffActions.less';

const StaffActions = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const profile = useAppSelector(selectCurrentProfile);
    const account = profile?.account;
    const membershipID = useAppSelector(selectActiveMembershipId);
    const isMCT = useAppSelector((state) => selectHasStaffRole(state, ['member-care', 'developer']));
    const [isMigrated, isMigratedLoading] = useHasJail({profile, isStaff: true, jailName: 'data pushed to v7'});

    const [getProfile] = useLazyGetProfileQuery();
    const [putInJail, {isError: isJailError, error: jailError, isSuccess: isJailSuccess, isLoading: isJailLoading, reset: jailReset, originalArgs: jailArgs}] = usePutInJailMutation();
    const [deleteApplicant, {isSuccess: deleteSuccess, isError: isDeleteError, isLoading: isDeleteLoading, error: deleteError, data: deleteData, reset: deleteReset}] = useDeleteMemberMutation();
    const [migrateToV7, {data: migrateReturn, isError: isMigrateError, error: migrateError, isSuccess: isMigrateSuccess, isLoading: isMigrateLoading, reset: resetMigrate}] = useMigrateMemberToV7Mutation();
    const [closeAccount, {isError: isCloseError, error: closeError, isSuccess: isCloseSuccess, isLoading: isCloseLoading, reset: resetClose}] = useCloseAccountMutation();
    const [cancelBookings, {isError: isCancelError, error: cancelError, isSuccess: isCancelSuccess, isLoading: isCancelLoading, reset: resetCancel}] = useCancelMemberBookingsMutation();
    const [getBookings, {data: bookingsReturn}] = useLazyGetBookingsQuery();

    const isSpinning = isDeleteLoading || isJailLoading || isMigrateLoading || isMigratedLoading || isCloseLoading || isCancelLoading;

    React.useEffect(() => {
        if (isJailSuccess) {
            showMessages(getJailSuccessMessage(jailArgs?.jailName), messageType.success);
            jailReset();
        } else if (isJailError) {
            let message = (jailError as BaseError).data?.message;
            if (message === 'Model already has this restriction.') {
                message = getJailErrorMessage(jailArgs?.modelType);
            }
            showMessages(message, messageType.error);
            jailReset();
        }
    }, [isJailError, isJailSuccess, jailArgs, jailError, jailReset]);

    React.useEffect(() => {
        if (deleteSuccess && deleteData) {
            showMessages(deleteData.message, messageType.success);
            deleteReset();
            dispatch(clearActiveMemberStaff());
            navigate('/member_change');
        } else if (isDeleteError && deleteError) {
            showMessages((deleteError as BaseError).data?.message, messageType.error);
            deleteReset();
        }
    }, [deleteSuccess, isDeleteError, deleteError, deleteReset, dispatch, navigate, deleteData]);

    React.useEffect(() => {
        if (isMigrateSuccess) {
            let migrateMessage = migrateToV7SuccessMessage;
            let type = messageType.success;
            if (migrateReturn && migrateReturn['Warning']) {
                // Custom success message if warning is returned.
                migrateMessage = migrateReturn['Warning'];
                type = messageType.warn;
            }
            showMessages(migrateMessage, type);
            profile && getProfile(profile.id); // Need to replenish the current profile.account for the new legacy_account_number.
            resetMigrate();
        } else if (isMigrateError) {
            showMessages((migrateError as BaseError).data?.message, messageType.error);
            resetMigrate();
        }
    }, [getProfile, isMigrateError, isMigrateSuccess, migrateError, migrateReturn, profile, resetMigrate]);

    React.useEffect(() => {
        if (isCloseSuccess) {
            let closeMessage = closeAccountSuccessMessage;
            let type = messageType.success;
            showMessages(closeMessage, type);

        } else if (isCloseError) {
            showMessages((closeError as BaseError).data?.message, messageType.error);
            resetClose();
        }
    }, [isCloseSuccess, closeError, isCloseError, resetClose]);

    React.useEffect(() => {
        if (isCancelSuccess) {
            let cancelMessage = cancelMemberBookingsSuccessMessage;
            let type = messageType.success;
            showMessages(cancelMessage, type);

        } else if (isCancelError) {
            showMessages((cancelError as BaseError).data?.message, messageType.error);
            resetCancel();
        }
    }, [isCancelError, isCancelSuccess, cancelError, resetCancel]);

    React.useEffect(() => {
        if (bookingsReturn?.data && bookingsReturn?.data.length > 0) {
            showMessages('Member has active bookings. These will not be cancelled.', messageType.warn);
        }
    }, [bookingsReturn]);

    const handleCancelBookings = () => {
        getBookings({
            page: 1,
            pageSize: 10,
            startedAt: formatTimestamp(dayjs()),
            endedAt: formatTimestamp(dayjs()),
            searchType: 'contains',
            state: 'CONFIRMED',
            membershipID: membershipID,
        });

        let now = dayjs().startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ');
        cancelBookings({id: membershipID, booking_started_at: now});
    }

    return (
        <Spin spinning={isSpinning}>
            <Row gutter={16}>
                <Col {...staffActionColSize}>
                    <StaffActionCard
                        cardTitle="Cancel Future Bookings"
                        toolTipMessage={cancelMemberBookingsToolTip}
                        confirmText={{
                            title: 'Cancel all future bookings for this membership?',
                            description: 'This action is irreversible.',
                        }}
                        action={() => handleCancelBookings()}
                    />
                </Col>
                <Col {...staffActionColSize}>
                {account && (
                    <StaffActionCard
                        cardTitle="Close Account"
                        toolTipMessage={closeAccountTooltip}
                        confirmText={{
                            title: 'Close this Account?',
                            description: closeAccountDescription,
                        }}
                        action={() => closeAccount(account.id)}
                    />
                )}
                </Col>
                <Col {...staffActionColSize}>
                {profile && (
                    <StaffActionCard
                        cardTitle="Revoke Create Booking"
                        toolTipMessage={revokeCreateBookingToolTip}
                        confirmText={null}
                        action={() => putInJail({
                            jailName: 'booking create restricted',
                            startedAt: dayjs().format('YYYY-MM-DD HH:mm:ssZZ'),
                            reason: restrictionAddedByStaff,
                            modelType: 'profile',
                            modelID: profile.id,
                        })}
                    />
                )}
                </Col>
                <Col {...staffActionColSize}>
                {account && (
                    <StaffActionCard
                        cardTitle="Waive Monthly Fee"
                        toolTipMessage={waiveMonthlyFeeToolTip}
                        confirmText={null}
                        action={() => putInJail({
                            jailName: 'waive monthly fee',
                            startedAt: dayjs().format('YYYY-MM-DD HH:mm:ssZZ'),
                            reason: restrictionAddedByStaff,
                            modelType: 'account',
                            modelID: account.id,
                        })}
                    />
                )}
                </Col>
                <Col {...staffActionColSize}>
                {account && (
                    <StaffActionCard
                        cardTitle="Make Tax Exempt"
                        toolTipMessage={makeTaxExemptToolTip}
                        confirmText={null}
                        action={() => putInJail({
                            jailName: 'tax exempt',
                            startedAt: dayjs().format('YYYY-MM-DD HH:mm:ssZZ'),
                            reason: restrictionAddedByStaff,
                            modelType: 'account',
                            modelID: account?.id,
                        })}
                    />
                )}
                </Col>
                {isMCT && !isMigrated && profile && (
                    <Col {...staffActionColSize}>
                        <StaffActionCard
                            cardTitle="Migrate Data to V7"
                            toolTipMessage={migrateToV7ToolTip}
                            confirmText={{
                                title: 'Are you sure?',
                                description: 'This action is permanent.'
                            }}
                            action={() => migrateToV7(profile.id)}
                        />
                    </Col>
                )}
                <Col {...staffActionColSize}>
                {isMCT && profile && (
                    <StaffActionCard
                        cardTitle="Delete Application"
                        toolTipMessage={deleteApplicantToolTip}
                        confirmText={{
                            title: 'Are you sure you want to delete this applicant?',
                            description: deleteApplicantDescription
                        }}
                        action={() => deleteApplicant(profile.id)}
                    />
                )}
                </Col>
            </Row>
        </Spin>
    )
};

const getJailSuccessMessage = (jailName?: string) => {
    switch (jailName) {
        case 'waive monthly fee':
            return waiveMonthlyFeeSuccessMessage;
        case 'tax exempt':
            return taxExemptSuccessMessage;
        default:
            return restrictionAppliedSuccessMessage;
    }
}

const getJailErrorMessage = (modelType?: AccountOrProfile) => {
    const model = modelType === 'profile' ? 'Profile' : 'Account';
    return `${model} already has this restriction`;
}

export default StaffActions;
